import React from "react";
import LegalLayout, { H2, P, UL } from "@/components/LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" updated="February 24, 2026">
      <P>
        This Privacy Policy explains what personal data ContentOS collects, how we use it, who we share it with, and the rights you have. If you have any questions, email <a className="text-primary underline" href="mailto:support@contentos.digital">support@contentos.digital</a>.
      </P>

      <H2>1. Who We Are</H2>
      <P>
        ContentOS is operated by <strong>ContentOS Digital LLC</strong> ("we", "us"). We are the data controller for the personal data described in this Policy.
      </P>

      <H2>2. What We Collect</H2>
      <P><strong>Account data.</strong> Your name, email, hashed password, and login timestamps.</P>
      <P><strong>Business intake data.</strong> Information you enter during onboarding — details about your business, offer, customer, brand voice, and any transcripts, testimonials, or reviews you paste in.</P>
      <P><strong>Content data.</strong> Content plans, hooks, scripts, and performance metrics you generate, import, or manage in the Service.</P>
      <P><strong>Billing data.</strong> Subscription tier, subscription status, and billing period metadata. Your card and full billing address are collected and stored by our payment processor, Paddle — we never see or store your card number.</P>
      <P><strong>Technical data.</strong> IP address, browser, device, and usage logs necessary to secure and operate the Service.</P>
      <P><strong>Third-party account tokens.</strong> If you connect Instagram, LinkedIn, or YouTube via OAuth, we store the access and refresh tokens necessary to pull the analytics you authorize.</P>

      <H2>3. How We Use Your Data</H2>
      <UL>
        <li>To provide, personalize, and improve the Service (e.g., generate your Brand Intelligence Profile, weekly plans, and reviews).</li>
        <li>To process payments and manage your subscription (via Paddle).</li>
        <li>To communicate with you about your account, billing, and product updates.</li>
        <li>To detect, prevent, and address technical or security issues.</li>
        <li>To comply with legal obligations.</li>
      </UL>

      <H2>4. AI Processing</H2>
      <P>
        Text you enter (onboarding intake, post metrics, and generation prompts) is transmitted to <strong>Anthropic</strong>'s Claude API to produce Output. Under our commercial agreement with the model provider, your inputs and Outputs are not used to train underlying foundation models. Aggregated, non-identifying usage metadata may be retained by the model provider for abuse prevention and reliability.
      </P>

      <H2>5. Who We Share Data With</H2>
      <UL>
        <li><strong>Anthropic</strong> — AI model provider processing your prompts and returning Output.</li>
        <li><strong>Paddle</strong> — payment processor and Merchant of Record; handles card processing, tax, and fraud checks.</li>
        <li><strong>MongoDB Atlas</strong> — cloud database provider that stores your account, intake, and generated content data.</li>
        <li><strong>Hosting provider</strong> — the cloud infrastructure on which the Service runs.</li>
        <li><strong>Third-party platforms you connect</strong> — Meta (Instagram), LinkedIn, and Google (YouTube) receive requests for the metrics and posts you authorize us to access.</li>
      </UL>
      <P>
        We do not sell your personal data. We do not share your Content Data with advertisers.
      </P>

      <H2>6. International Transfers</H2>
      <P>
        Your data may be processed in countries other than your own, including the United States. Where required, we rely on standard contractual clauses or equivalent safeguards for such transfers.
      </P>

      <H2>7. Data Retention</H2>
      <P>
        We keep your data for as long as your account is active. If you delete your account, we delete or anonymize your personal data within 30 days, except where we are required to retain it (e.g., for tax, fraud prevention, or legal claims). Third-party account tokens are deleted immediately when you disconnect a platform.
      </P>

      <H2>8. Your Rights</H2>
      <P>
        Depending on your jurisdiction, you may have the right to access, correct, delete, port, or restrict processing of your personal data, and to object to certain processing or withdraw consent. Contact <a className="text-primary underline" href="mailto:support@contentos.digital">support@contentos.digital</a> to exercise these rights. If you are in the EEA/UK, you have the right to lodge a complaint with your local supervisory authority.
      </P>

      <H2>9. Cookies</H2>
      <P>
        We use a minimal set of cookies and local-storage keys necessary to keep you signed in and remember pending purchases across the checkout flow. We do not use advertising cookies. Analytics events, where used, are aggregated and do not include your email or password.
      </P>

      <H2>10. Children</H2>
      <P>
        The Service is not directed to children under 18. We do not knowingly collect personal data from anyone under 18. If you believe a child has provided us with data, email us and we will delete it.
      </P>

      <H2>11. Security</H2>
      <P>
        We use industry-standard technical and organizational measures to protect your data, including encryption in transit (HTTPS), hashed passwords (bcrypt), scoped access tokens, and least-privilege access to production systems. No system is 100% secure — please notify us immediately of any suspected compromise.
      </P>

      <H2>12. Changes to this Policy</H2>
      <P>
        We may update this Policy from time to time. If we make material changes we will notify you by email or in-app notice. Your continued use of the Service after the changes take effect constitutes acceptance.
      </P>

      <H2>13. Contact</H2>
      <P>
        Privacy questions or requests: <a className="text-primary underline" href="mailto:support@contentos.digital">support@contentos.digital</a>.
      </P>
    </LegalLayout>
  );
}
